import { hubConfig, timingConfig } from "../config";
import {
	authenticationHelper,
	clientHelper,
	communicationHelper,
	documentHelper,
	formatHelper,
	iframeHelper,
	storageHelper,
} from "../helper";

let currentLocation, settings;
let options = {
	[hubConfig.controllerOptions.onWidgetOpen]: () => undefined,
	[hubConfig.controllerOptions.onWidgetClose]: () => undefined,
	disableListeners: false,
	disableNotifications: false,
};

//EMIT PAGE CHANGES
function emitCloseTab(frames) {
	communicationHelper.broadcastMessage({
		frames,
		payload: {
			type: hubConfig.emitter.closeTab,
		},
	});
}
function emitLocationChange({ frames, location, error }) {
	const type = hubConfig.emitter.locationChange;
	let payload = { error };

	if (!error) {
		const coords = {
			accuracy: location.coords.accuracy,
			latitude: location.coords.latitude,
			longitude: location.coords.longitude,
		};

		payload = { location: { coords, timestamp: location.timestamp } };
	}

	communicationHelper.broadcastMessage({ frames, payload: { type, payload } });
}
function emitLanguageChange({ frames, payload }) {
	const type = hubConfig.emitter.languageChange;

	handleLanguageSupport({ language: payload.language, frames });
	communicationHelper.broadcastMessage({ frames, payload: { type, payload } });
}
function emitParentSizeChange(frames) {
	communicationHelper.broadcastMessage({
		frames,
		payload: {
			type: hubConfig.emitter.parentSize,
			payload: { size: formatHelper.formattedWindowSize() },
		},
	});
}
function emitUrlChange({ frames, payload }) {
	const type = hubConfig.emitter.urlChange;

	communicationHelper.broadcastMessage({ frames, payload: { type, payload } });
}
function emitOpenWidget({ frames, payload }) {
	const type = hubConfig.emitter.openWidget;

	communicationHelper.broadcastMessage({ frames, payload: { type, payload } });
}
function emitCloseWidget({ frames, payload }) {
	const type = hubConfig.emitter.closeWidget;

	communicationHelper.broadcastMessage({ frames, payload: { type, payload } });
}

//EMIT
async function emitWidgetInitialization({ frames, apiKey, data, open }) {
	const type = hubConfig.emitter.initializeWidget;
	const { id: identityId, contactId } = await authenticationHelper.identifyContact({ apiKey, data });
	const payload = {
		apiKey,
		data,
		identityId,
		contactId,
		state: storageHelper.getState(),
	};
	const interval = setInterval(() => {
		if (settings) {
			return clearInterval(interval);
		}

		communicationHelper.broadcastMessage({ frames, payload: { type, payload } });
		open && emitOpenWidget({ frames });
	}, timingConfig.second);
	watchUrlChange(frames);
}
async function emitFlowInitialization({ frames, apiKey, data }) {
	let payload = {
		apiKey,
		data,
		state: storageHelper.getState(),
		identityId: storageHelper.getIdentity(),
	};

	setTimeout(() => {
		communicationHelper.broadcastMessage({
			frames,
			payload: {
				type: hubConfig.emitter.initializeWidget,
				payload,
			},
		});
	}, timingConfig.initTimeout);

	await authenticationHelper.authenticateApiKey(apiKey);

	const { id: identityId, contactId } = await authenticationHelper.identifyContact({ apiKey, data });

	payload = {
		apiKey,
		data,
		identityId,
		contactId,
		state: storageHelper.getState(),
	};

	communicationHelper.broadcastMessage({
		frames,
		payload: {
			type: hubConfig.emitter.identityVerified,
			payload: {
				...payload,
				contactId,
				identityId,
			},
		},
	});
}

function emitRefresh(frames) {
	communicationHelper.broadcastMessage({ frames, payload: { type: hubConfig.emitter.refresh } });
}
function emitRequestDesktopNotification({ frames, result }) {
	const type = hubConfig.emitter.desktopNotificationPermission;
	const payload = { permission: result };

	communicationHelper.broadcastMessage({ frames, payload: { type, payload } });
}

//WATCH
function watchUrlChange(frames) {
	setInterval(() => {
		if (window.location.href === currentLocation) {
			return;
		}

		const payload = { location: formatHelper.formattedLocation() };

		currentLocation = window.location.href;
		emitUrlChange({ frames, payload });
	}, timingConfig.second);
}
function watchWindowResize(frames) {
	emitParentSizeChange(frames);
	window.addEventListener("resize", () => {
		if (options.disableListeners) {
			return;
		}

		emitParentSizeChange(frames);
	});
}

//LISTENERS
function addMessageListener(frames) {
	window.addEventListener("message", (event) => {
		const { type, payload } = event.data;
		const frameName = authenticationHelper.authorizeEventOrigin(event.origin);
		// uncomment for debug
		// console.groupCollapsed(type);
		// console.log(payload);
		// console.groupEnd();

		if (
			(!frameName || options.disableListeners || window.Bothive.widget.hidden) &&
			type !== hubConfig.listener.settingsChange
		) {
			return;
		}

		switch (type) {
			case hubConfig.listener.requestDeskNotificationPermission:
				documentHelper
					.requestDeskNotificationPermission()
					.then((result) => emitRequestDesktopNotification({ frames, result }));
				break;
			case hubConfig.listener.cancelLocationWatch:
				clientHelper.cancelLocationWatch();
				break;
			case hubConfig.listener.requestLocation:
				clientHelper.getLocation((props) => emitLocationChange({ frames, ...props }));
				break;
			case hubConfig.listener.requestParentSize:
				emitParentSizeChange(frames);
				break;
			case hubConfig.listener.sendDesktopNotification:
				documentHelper.sendDesktopNotification({
					title: payload.title,
					message: payload.message,
					options: payload.options,
				});
				break;
			case hubConfig.listener.startTabbarNotification:
				if (options.disableNotifications) {
					break;
				}

				documentHelper.startNotificationInDocumentTitle(payload.message);
				break;
			case hubConfig.listener.stopTabbarNotification:
				documentHelper.stopNotificationInDocumentTitle();
				break;
			case hubConfig.listener.styleChange:
				iframeHelper.changeStyle({ frame: frames[frameName], style: payload.style });
				break;
			case hubConfig.listener.hideWidget:
				iframeHelper.hideFrames(frames);
				break;
			case hubConfig.listener.showWidget:
				if (window.Bothive.widget.hidden) {
					break;
				}

				iframeHelper.showFrames(frames);
				break;
			case hubConfig.listener.closeWidget:
				options.onWidgetClose();
			// eslint-disable-next-line no-fallthrough
			case hubConfig.listener.openWidget:
				options.onWidgetOpen();
			// eslint-disable-next-line no-fallthrough
			case hubConfig.listener.newMessage:
				if (window.Bothive.widget.hidden) {
					break;
				}

				if (options.disableNotifications) {
					communicationHelper.sendMessageToFrame({ frame: frames.widget, payload: event.data });
					break;
				}

				communicationHelper.broadcastMessage({ frames, payload: event.data });
				break;
			case hubConfig.listener.settingsChange:
				settings = payload;
				window.loaded = true;
				storageHelper.setState(payload.settings);
				communicationHelper.broadcastMessage({ frames, payload: event.data });
				styleChange({ frames, payload: payload.settings });
				break;
			case hubConfig.listener.resetMessages:
				communicationHelper.broadcastMessage({ frames, payload: event.data });
				break;
		}
	});
}
function enableListeners() {
	options.disableListeners = false;
}

export default {
	initHub(frames) {
		enableListeners();
		addMessageListener(frames);
		setTimeout(() => watchWindowResize(frames), timingConfig.debounce);
		window.addEventListener("beforeunload", () => emitCloseTab(frames));
	},
	initFlowHub(frames) {
		enableListeners();
		addMessageListener(frames);
	},
	disableListeners() {
		options.disableListeners = true;
	},
	enableListeners,
	enableNotifications() {
		options.disableNotifications = false;
	},
	disableNotifications() {
		options.disableNotifications = true;
		documentHelper.stopNotificationInDocumentTitle();
	},
	emitLanguageChange,
	checkLanguageIsSupported,
	emitWidgetInitialization,
	emitFlowInitialization,
	emitOpenWidget,
	emitCloseWidget,
	emitRefresh,
	emitCloseTab,
	watchIfDocumentIsReady(callback) {
		if (document.readyState === "complete" || document.readyState !== "loading") {
			callback();
			return;
		}

		if (window.addEventListener) {
			window.addEventListener("DOMContentLoaded", () => callback());
			return;
		}

		window.addEventListener("onreadystatechange", () => {
			if (document.readyState === "loading") return;

			callback();
		});
	},
	setControllerOptions({ key, value }) {
		options[key] = value;
	},
	updateLanguage({ language, frames }) {
		window.language = language;
		emitLanguageChange({ frames, payload: { language } });
		handleLanguageSupport({ language, frames });
	},
};

function styleChange({ frames, payload }) {
	if (
		payload.settings &&
		!window.Bothive.widget.forceShow &&
		(payload.settings.hideWidget || (payload.settings.hideOnOffline && !payload.settings.isOpen))
	) {
		return iframeHelper.hideFrames(frames);
	}

	handleLanguageSupport({ frames });
}

function checkLanguageIsSupported(language) {
	try {
		const state = storageHelper.getState();

		if (
			!state ||
			!state.settings ||
			!state.settings.hideUnsupportedLanguage ||
			!state.settings.languages ||
			!state.settings.languages.length
		) {
			return true;
		}

		return !!state.settings.languages.find((item) => item && item.includes(language || ""));
	} catch (_) {
		return true;
	}
}
function handleLanguageSupport({ language = undefined, frames }) {
	const state = storageHelper.getState();
	const lang = language || window.language || state.language;
	const isLanguageSupported = checkLanguageIsSupported(lang);

	if (window.Bothive.widget.forceHide || (state.settings && state.settings.hideWidget)) {
		return;
	}

	if (!isLanguageSupported) {
		return iframeHelper.hideFrames(frames);
	}

	if (isLanguageSupported && window.Bothive.widget.hidden) {
		return iframeHelper.showFrames(frames);
	}
}
